<template>
    <x-page title="PDF">
        <template #header-actions>
            <x-create-button
                url="/admin/translation-upload-batches/create"
                :disabled="isLoading"
                v-if="can('createTranslationUploadBatches')"
            />
        </template>

        <x-table
            :headers="['#', 'Nama', 'Konten', 'Status', '']"
            :is-loading="isLoading"
        >
            <tr v-for="(resource, index) in resources" :key="resource.id">
                <td>{{ rowIndex(index, $route.query) }}</td>
                <td class="nowrap">
                    <router-link
                        :to="`/admin/translation-upload-batches/${resource.id}`"
                        title="Klik untuk lihat detail"
                    >
                        {{ resource.display_name }}
                    </router-link>
                    <p
                        v-if="
                            resource.status === 'translating' ||
                            resource.status === 'translating_in_progress'
                        "
                        class="text-muted"
                    >
                        Sedang di-translate, Anda boleh meninggalkan halaman ini
                        dan cek kembali nanti.
                    </p>
                </td>
                <td>
                    <router-link
                        :to="`/admin/translation-drafts?batch_id=${resource.id}`"
                        title="Lihat Konten"
                    >
                        {{ resource.drafts_count }} konten
                    </router-link>
                </td>
                <td>
                    {{ resource.status }}
                </td>
                <td>
                    <template
                        v-if="resource.status !== 'translating_in_progress'"
                    >
                        <x-quick-action-button
                            icon="language"
                            @click="translateResource(resource)"
                            :disabled="isLoading"
                            v-if="
                                can('translateTranslationDrafts') &&
                                resource.drafts_count > 0 &&
                                resource.status !== 'translating'
                            "
                            title="Translate"
                        />
                        <x-quick-action-button
                            icon="magic"
                            @click="openConversionModal(resource)"
                            :disabled="isLoading"
                            v-if="
                                can('convertTranslationDrafts') &&
                                resource.drafts_count > 0 &&
                                resource.status !== 'translating' &&
                                resource.status !== 'converted'
                            "
                            title="Publish"
                        />
                        <x-quick-action-button
                            icon="file-text"
                            title="Ekstrak PDF"
                            :disabled="isLoading"
                            v-if="resource.status === 'pending'"
                            @click="extractPdf(resource)"
                        />
                        <x-quick-edit-button
                            :url="`/admin/translation-upload-batches/${resource.id}/edit`"
                            :disabled="isLoading"
                            v-if="can('updateTranslationUploadBatches')"
                        />
                        <x-quick-delete-button
                            @click="deleteResource(resource)"
                            :disabled="isLoading"
                            v-if="can('deleteTranslationUploadBatches')"
                        />
                    </template>
                </td>
            </tr>
        </x-table>

        <translation-upload-batch-conversion-modal
            id="conversion-modal"
            :resource="selectedResource"
            @converted="() => fetchResources()"
        />

        <x-pagination
            v-if="shouldRenderPagination"
            :pagination="meta"
            :query="$route.query"
        />
    </x-page>
</template>

<script>
import XCreateButton from "../../components/XCreateButton.vue";
import XQuickDeleteButton from "../../components/XQuickDeleteButton.vue";
import XFormGroup from "../../components/XFormGroup.vue";
import XPage from "../../components/XPage.vue";
import XPagination from "../../components/XPagination.vue";
import XTable from "../../components/XTable.vue";
import {
    deletePrompt,
    handleGeneralXhrError,
    handleValidationXhrError,
    warningPrompt,
} from "../../helpers";
import FilterableMixin from "../../mixins/FilterableMixin";
import PaginationMixin from "../../mixins/PaginationMixin";
import PermissionMixin from "../../mixins/PermissionMixin";
import XQuickActionButton from "../../components/XQuickActionButton.vue";
import XQuickEditButton from "../../components/XQuickEditButton.vue";
import TranslationUploadBatchConversionModal from "./TranslationUploadBatchConversionModal.vue";

const defaultFilters = {};

export default {
    beforeRouteUpdate(to, from, next) {
        this.fetchResources(to);
        next();
    },
    components: {
        XCreateButton,
        XTable,
        XQuickDeleteButton,
        XPage,
        XFormGroup,
        XPagination,
        XQuickActionButton,
        XQuickEditButton,
        TranslationUploadBatchConversionModal,
    },
    created() {
        this.preselectFilters(defaultFilters);
        this.handleFilter().catch(() => {
            this.fetchResources();
            setInterval(() => {
                this.fetchResources();
            }, 10000);
        });
    },
    data() {
        return {
            filters: { ...defaultFilters },
            isLoading: false,
            resources: [],
            selectedResource: null,
        };
    },
    methods: {
        deleteResource(resource) {
            deletePrompt({
                name: resource.display_name,
                onConfirm: () => {
                    this.isLoading = true;
                    const request = axios
                        .delete(
                            `/api/translation-upload-batches/${resource.id}`
                        )
                        .then(() => {
                            this.$notify({
                                type: "success",
                                title: `Berhasil`,
                                text: `${resource.display_name} dihapus!`,
                            });
                            this.fetchResources();
                        });
                    handleGeneralXhrError(request).finally(() => {
                        this.isLoading = false;
                    });
                },
            });
        },
        extractPdf(resource) {
            this.isLoading = true;
            const request = axios
                .post(`/api/translation-upload-batches/${resource.id}/extract`)
                .then((response) => {
                    this.$notify({
                        type: "success",
                        title: `Berhasil`,
                        text: `${resource.display_name} berhasil diproses!`,
                    });
                    this.fetchResources();
                });
            return handleValidationXhrError(request).finally(() => {
                this.isLoading = false;
            });
        },
        fetchResources(route = this.$route) {
            this.isLoading = true;
            const request = axios
                .get("/api/translation-upload-batches", {
                    params: { ...route.query, with_count: ["drafts"] },
                })
                .then((response) => {
                    this.$set(this, "resources", response.data.data);
                    this.setMeta(response);
                });
            return handleGeneralXhrError(request).finally(() => {
                this.isLoading = false;
            });
        },
        handleFilter() {
            return this.$router.push({
                path: this.$route.path,
                query: _.pickBy({
                    ...this.filters,
                    page: 1,
                    without_global_scopes: 1,
                }),
            });
        },
        openConversionModal(resource) {
            this.selectedResource = resource;
            $("#conversion-modal").modal("show");
        },
        resetFilter() {
            this.filters = { ...defaultFilters };
            this.handleFilter();
        },
        translateResource(resource) {
            warningPrompt({
                title: "Translate Upload Batch",
                text: `Translate ${resource.display_name}? Action ini akan dikenakan biaya translasi untuk ${resource.drafts_count} konten.`,
                onConfirm: () => {
                    this.isLoading = true;
                    const request = axios
                        .post(
                            `/api/translation-upload-batches/${resource.id}/translate`
                        )
                        .then(() => {
                            this.$notify({
                                type: "success",
                                title: `Berhasil`,
                                text: `Translate ${resource.display_name} dimulai, mohon tunggu beberapa saat`,
                            });
                            this.fetchResources();
                        });
                    handleValidationXhrError(request).finally(() => {
                        this.isLoading = false;
                    });
                },
            });
        },
    },
    mixins: [FilterableMixin, PaginationMixin, PermissionMixin],
};
</script>
